import { useEffect } from 'react';

const DEFAULT_TITLE = "Skill Ascent | Top Online Skill Development & Industry Training Courses India";
const DEFAULT_DESCRIPTION = "Skill Ascent is India's leading online skill development platform offering job-oriented training in IT, Python Full Stack, Data Science, AI/ML, Digital Marketing, Graphic Design, Video Editing, Agriculture, Engineering & Healthcare with recognized certificates.";
const DEFAULT_KEYWORDS = "Skill Ascent, online courses India, full stack development course, data science training, AI ML courses, digital marketing course online with certificate, graphic design certification, video editing course, IT training institute India, job oriented courses";
const DEFAULT_IMAGE = "https://www.skillascent.in/images/og-banner.png";
const BASE_URL = "https://www.skillascent.in";

const setMetaTag = (attrName, attrValue, content) => {
    if (!content) return;
    let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
};

const setCanonicalTag = (url) => {
    if (!url) return;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
    }
    link.setAttribute('href', url);
};

const setJsonLdSchema = (schemaData) => {
    const existingScript = document.getElementById('page-jsonld-schema');
    if (existingScript) {
        existingScript.remove();
    }

    if (schemaData) {
        const script = document.createElement('script');
        script.id = 'page-jsonld-schema';
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schemaData);
        document.head.appendChild(script);
    }
};

const SEO = ({
    title,
    description,
    keywords,
    canonicalPath = "",
    ogImage,
    ogType = "website",
    robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    schemaData = null,
    breadcrumbs = null
}) => {
    useEffect(() => {
        const finalTitle = title ? (title.includes('Skill Ascent') ? title : `${title} | Skill Ascent`) : DEFAULT_TITLE;
        const finalDesc = description || DEFAULT_DESCRIPTION;
        const finalKeywords = keywords || DEFAULT_KEYWORDS;
        const finalImage = ogImage || DEFAULT_IMAGE;
        const finalUrl = canonicalPath ? `${BASE_URL}${canonicalPath.startsWith('/') ? canonicalPath : '/' + canonicalPath}` : BASE_URL;

        // Update Document Title
        document.title = finalTitle;

        // Primary Meta Tags
        setMetaTag('name', 'title', finalTitle);
        setMetaTag('name', 'description', finalDesc);
        setMetaTag('name', 'keywords', finalKeywords);
        setMetaTag('name', 'robots', robots);
        setCanonicalTag(finalUrl);

        // Open Graph / Facebook / WhatsApp
        setMetaTag('property', 'og:site_name', 'Skill Ascent');
        setMetaTag('property', 'og:type', ogType);
        setMetaTag('property', 'og:url', finalUrl);
        setMetaTag('property', 'og:title', finalTitle);
        setMetaTag('property', 'og:description', finalDesc);
        setMetaTag('property', 'og:image', finalImage);
        setMetaTag('property', 'og:image:secure_url', finalImage);
        setMetaTag('property', 'og:image:type', finalImage.endsWith('.png') ? 'image/png' : 'image/jpeg');
        setMetaTag('property', 'og:image:width', '1200');
        setMetaTag('property', 'og:image:height', '630');
        setMetaTag('property', 'og:image:alt', finalTitle);
        setMetaTag('property', 'og:locale', 'en_IN');

        // Twitter Cards
        setMetaTag('name', 'twitter:card', 'summary_large_image');
        setMetaTag('name', 'twitter:url', finalUrl);
        setMetaTag('name', 'twitter:title', finalTitle);
        setMetaTag('name', 'twitter:description', finalDesc);
        setMetaTag('name', 'twitter:image', finalImage);

        // Combine Schemas into a Single Graph if breadcrumbs or multiple schemas are present
        let finalSchema = schemaData;
        if (breadcrumbs && breadcrumbs.length > 0) {
            const breadcrumbSchema = {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": breadcrumbs.map((item, index) => ({
                    "@type": "ListItem",
                    "position": index + 1,
                    "name": item.name,
                    "item": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url.startsWith('/') ? item.url : '/' + item.url}`
                }))
            };

            if (schemaData) {
                if (schemaData["@graph"]) {
                    finalSchema = {
                        "@context": "https://schema.org",
                        "@graph": [...schemaData["@graph"], breadcrumbSchema]
                    };
                } else {
                    finalSchema = {
                        "@context": "https://schema.org",
                        "@graph": [schemaData, breadcrumbSchema]
                    };
                }
            } else {
                finalSchema = breadcrumbSchema;
            }
        }

        setJsonLdSchema(finalSchema);

        return () => {
            const pageSchema = document.getElementById('page-jsonld-schema');
            if (pageSchema) {
                pageSchema.remove();
            }
        };
    }, [title, description, keywords, canonicalPath, ogImage, ogType, robots, schemaData, breadcrumbs]);

    return null;
};

export default SEO;

